import React, { useEffect, useState, useMemo, useRef } from 'react'

import { useQueryClient } from '@tanstack/react-query'
import { DateTime } from 'luxon'
import { useSelector, useDispatch } from 'react-redux'
import {
	useLocation,
	useParams,
	useNavigate,
	useSearchParams,
} from 'react-router-dom'

import { UpdateZoneRequestDTO } from '@/core/api/dtos/request/zone/update'
import { Group } from '@/core/api/entities/group'
import { Playlist } from '@/core/api/entities/playlist/playlist'
import { Schedule } from '@/core/api/entities/schedule'
import { Zone } from '@/core/api/entities/zone/zone'
import {
	isAccessDeniedError,
	isForbiddenError,
	shouldRetryRequest,
} from '@/core/api/utils/httpErrors'
import { useAccountConfig } from '@/core/hooks/useAccountConfig'
import { useUpdateGroupZones, useGroups } from '@/core/hooks/useGroup'
import useRoutePlayerControl from '@/core/hooks/useRoutePlayerControl'
import {
	useExportZoneConnections,
	useGetZoneConnections,
	useGetZoneEvents,
	useGetZonePlaylists,
	useGetZoneSchedules,
	useAddPlaylistsToZone,
	useAddSchedulesToZone,
	useRemovePlaylistsFromZone,
	useRemoveSchedulesFromZone,
	useUpdateZone,
	useZone,
} from '@/core/hooks/useZone'
import { useTranslation } from '@/core/i18n/useTranslation'
import { RootState } from '@/core/store'
import {
	setActiveZone,
	clearActiveZone,
} from '@/core/store/slices/activeZoneSlice'
import { useGetOccupation } from '@/features/zones/hooks/useGetOccupation'
import { Button } from '@/shared/components/atoms/Button'
import ConfirmationModal from '@/shared/components/molecules/ConfirmationModal/ConfirmationModal'
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from '@/shared/components/molecules/Dialog/dialog'
import { useNotificationContext } from '@/shared/context/NotificationProvider'
import { useCurrentAccount } from '@/shared/hooks/useCurrentAccount'
import { extractErrorMessage } from '@/shared/utils/helpers'

import { ZoneDraftProvider, useZoneDraft } from './context/ZoneDraftContext'
import ZoneDetailsUI from './ZoneDetailsUI'
import useZoneDeletion from '../../hooks/useZoneDeletion'
import useZoneRefresh from '../../hooks/useZoneRefresh'
import AddContentToZoneModal from '../../modals/AddContentToZoneModal'
import AddGroupToZone from '../../modals/AddGroupToZone'
import DeleteZoneModal from '../../modals/DeleteZoneModal'
import EditZoneModal from '../../modals/EditZoneModal'
import RefreshZoneModal from '../../modals/RefreshZoneModal'
import RemoveZoneFromGroupModal from '../../modals/RemoveZoneFromGroupModal'

type ZoneDetailsTab = 'information' | 'connection-status' | 'playback-history'
type GroupAssignedContentType = 'playlist' | 'schedule'
type ZoneContentTab = 'playlists' | 'schedules'
type ZoneDetailsLocationState = {
	openAddContentModal?: boolean
	addContentInitialTab?: ZoneContentTab
}

const ZoneDetailsContent: React.FC = () => {
	// Get the zoneId from the URL params
	const { zoneId } = useParams<{ zoneId: string }>()
	// Get search params to check for tab parameter
	const [searchParams, setSearchParams] = useSearchParams()

	// Get the navigate function from the useNavigate hook
	const navigate = useNavigate()
	const location = useLocation()

	// Get the dispatch function from the useDispatch hook
	const dispatch = useDispatch()

	// Get the draft context
	const zoneDraft = useZoneDraft()

	// Redirect to zones list if zoneId is not a number
	if (Number.isNaN(Number(zoneId))) {
		navigate('/zones')
	}

	// Close the player when navigating away from zone details
	// Keep it open only for the current zone detail route
	useRoutePlayerControl([`/zones/${zoneId}`])

	// Get the showNotification function from the useNotificationContext
	const { showNotification } = useNotificationContext()
	const currentAccount = useCurrentAccount()

	const hasAdvertAudioVersionFeature =
		currentAccount?.hasAdvertAudioVersionFeature() ?? false

	const zoneInclude =
		'box_allocations,state,groups.announcements,groups.schedules,groups.playlists,store.lastGooglePlaceData,last_played_song,last_played_schedule,advert_audio_version,flags'

	// Get translation function
	const { t } = useTranslation('common')
	const { t: tZones } = useTranslation('zones')

	// State for remove zone from group modal
	const [removeGroupModalVisible, setRemoveGroupModalVisible] = useState(false)
	const [selectedGroupToRemove, setSelectedGroupToRemove] =
		useState<Group | null>(null)

	// State for add zone to groups modal
	const [addToGroupsModalVisible, setAddToGroupsModalVisible] = useState(false)

	// State for edit zone modal
	const [editZoneModalVisible, setEditZoneModalVisible] = useState(false)
	const [accessDeniedModalOpen, setAccessDeniedModalOpen] = useState(false)
	const redirectToRef = useRef<string>('/zones')
	const [scheduleActionDeniedModalOpen, setScheduleActionDeniedModalOpen] =
		useState(false)
	const [playlistActionDeniedModalOpen, setPlaylistActionDeniedModalOpen] =
		useState(false)

	// Zone deletion hook
	const zoneDeletion = useZoneDeletion({
		redirectPath: '/zones',
		redirectTimeout: 2000,
	})

	// Zone refresh hook
	const zoneRefresh = useZoneRefresh({
		onSuccess: () => {
			refetchZone()
		},
	})

	// Get the tab from URL params if available
	const tabParam = searchParams.get('tab')

	// Set initial active tab based on URL parameter or default to 'zoneInformation'
	const mapQueryParamToTab = (param: string | null): ZoneDetailsTab => {
		switch (param) {
			case 'events':
				return 'playback-history'
			case 'connections':
				return 'connection-status'
			default:
				return 'information'
		}
	}

	const [activeTab, setActiveTab] = useState<ZoneDetailsTab>(
		mapQueryParamToTab(tabParam)
	)
	const [tabsVisited, setTabsVisited] = useState<
		Record<ZoneDetailsTab, boolean>
	>({
		information: true,
		'connection-status': activeTab === 'connection-status',
		'playback-history': activeTab === 'playback-history',
	})

	// State for the date range
	const [startDate, setStartDate] = useState<DateTime>(
		DateTime.now().minus({ days: 7 })
	)
	const [endDate, setEndDate] = useState<DateTime>(DateTime.now())

	useEffect(() => {
		setTabsVisited((prev) => {
			if (prev[activeTab]) {
				return prev
			}
			return {
				...prev,
				[activeTab]: true,
			}
		})
	}, [activeTab])

	const handleTabChange = (nextTab: ZoneDetailsTab) => {
		setActiveTab(nextTab)

		const mapTabToQueryParam = (tab: ZoneDetailsTab) => {
			switch (tab) {
				case 'connection-status':
					return 'connections'
				case 'playback-history':
					return 'events'
				default:
					return null
			}
		}

		const queryValue = mapTabToQueryParam(nextTab)
		const newParams = new URLSearchParams(searchParams)

		if (queryValue) {
			newParams.set('tab', queryValue)
		} else {
			newParams.delete('tab')
		}

		setSearchParams(newParams, { replace: true })
	}

	const activeZone = useSelector((state: RootState) => state.activeZone.zone)

	// Clear activeZone on component unmount
	useEffect(() => {
		return () => {
			dispatch(clearActiveZone())
		}
	}, [dispatch])

	// Get zone data using the useZone hook
	const {
		data: zone,
		isLoading,
		error,
		refetch: refetchZone,
	} = useZone({
		playerId: Number(zoneId),
		config: useAccountConfig({
			params: {
				include: zoneInclude,
			},
		}),
		options: {
			retry: shouldRetryRequest,
		},
	})

	// Warn before redirecting on 403/404 errors for zone queries
	useEffect(() => {
		if (!error) return
		if (!isAccessDeniedError(error)) return
		redirectToRef.current = '/zones'
		setAccessDeniedModalOpen(true)
	}, [error])

	// Set the active zone in the redux store
	useEffect(() => {
		if (zone) {
			dispatch(setActiveZone(zone))
		}
	}, [zone, dispatch])

	// Use the zone from the query or fall back to activeZone
	const currentZone = zone || activeZone

	const shouldLoadConnections =
		!!currentZone?.uuid &&
		(tabsVisited['connection-status'] ||
			activeTab === 'connection-status' ||
			tabsVisited['information'] ||
			activeTab === 'information')

	const {
		data: zoneConnections = [],
		isLoading: isZoneConnectionsLoading,
		isFetching: isZoneConnectionsFetching,
		error: zoneConnectionsError,
	} = useGetZoneConnections({
		uuid: currentZone?.uuid,
		config: useAccountConfig({
			params: {
				from: startDate.toISODate(),
				to: endDate.toISODate(),
				offset: -(startDate.offset || endDate.offset),
			},
		}),
		options: {
			enabled: shouldLoadConnections,
			retry: shouldRetryRequest,
		},
	})

	useEffect(() => {
		if (!zoneConnectionsError) return
		if (!isAccessDeniedError(zoneConnectionsError)) return
		redirectToRef.current = '/zones'
		setAccessDeniedModalOpen(true)
	}, [zoneConnectionsError])

	// Fetch zone events
	const shouldLoadEvents =
		!!currentZone?.uuid &&
		(tabsVisited['playback-history'] || activeTab === 'playback-history')

	const {
		data: zoneEventsData,
		isLoading: isZoneEventsLoading,
		isFetching: isZoneEventsFetching,
		error: zoneEventsError,
	} = useGetZoneEvents({
		uuid: currentZone?.uuid,
		config: useAccountConfig({
			params: {
				per_page: 50,
			},
		}),
		options: {
			enabled: shouldLoadEvents,
			retry: shouldRetryRequest,
		},
	})

	useEffect(() => {
		if (!zoneEventsError) return
		if (!isAccessDeniedError(zoneEventsError)) return
		redirectToRef.current = '/zones'
		setAccessDeniedModalOpen(true)
	}, [zoneEventsError])

	// Extract events from the paginated response or default to empty array
	const zoneEvents = zoneEventsData?.data || []

	// Fetch occupation data for smart zones
	const { data: occupationData, isLoading: isLoadingOccupation } =
		useGetOccupation({ zone: currentZone ?? undefined })

	// Fetch directly assigned playlists for the zone
	const queryClient = useQueryClient()
	const { data: directPlaylistsData, isLoading: isLoadingDirectPlaylists } =
		useGetZonePlaylists({
			zoneId: Number(zoneId),
			config: useAccountConfig({}),
			options: {
				enabled: !!zoneId,
			},
		})

	const directPlaylists: Playlist[] = useMemo(() => {
		if (!directPlaylistsData || !Array.isArray(directPlaylistsData)) return []
		// The data might be raw DTOs, so handle both cases
		return directPlaylistsData
	}, [directPlaylistsData])
	const { data: directSchedulesData } = useGetZoneSchedules({
		zoneId: Number(zoneId),
		config: useAccountConfig({}),
		options: {
			enabled: !!zoneId,
		},
	})
	const directSchedules: Schedule[] = useMemo(() => {
		if (!directSchedulesData || !Array.isArray(directSchedulesData)) return []
		return directSchedulesData
	}, [directSchedulesData])

	const initialPlaylistIds = useMemo(
		() => directPlaylists.map((playlist) => playlist.id),
		[directPlaylists]
	)
	const initialScheduleIds = useMemo(
		() => directSchedules.map((schedule) => schedule.id),
		[directSchedules]
	)

	const groupPlaylistConflicts = useMemo(() => {
		const conflicts = new Map<number, string>()
		currentZone?.groups?.forEach((group) => {
			group.playlists?.forEach((playlist) => {
				if (!conflicts.has(playlist.id)) {
					conflicts.set(playlist.id, group.name)
				}
			})
		})
		return conflicts
	}, [currentZone?.groups])

	const groupScheduleConflicts = useMemo(() => {
		const conflicts = new Map<number, string>()
		currentZone?.groups?.forEach((group) => {
			group.schedules?.forEach((schedule) => {
				if (!conflicts.has(schedule.id)) {
					conflicts.set(schedule.id, group.name)
				}
			})
		})
		return conflicts
	}, [currentZone?.groups])

	// Mutation to remove playlists from zone
	const {
		mutateAsync: removePlaylistsFromZone,
		isPending: isRemovingPlaylistFromZone,
	} = useRemovePlaylistsFromZone({
		config: useAccountConfig({}),
	})
	const { mutateAsync: addPlaylistsToZone } = useAddPlaylistsToZone({
		config: useAccountConfig({}),
	})

	// Mutation to remove schedules from zone
	const {
		mutateAsync: removeSchedulesFromZone,
		isPending: isRemovingScheduleFromZone,
	} = useRemoveSchedulesFromZone({
		config: useAccountConfig({}),
	})
	const { mutateAsync: addSchedulesToZone } = useAddSchedulesToZone({
		config: useAccountConfig({}),
	})

	// Playlist removal state
	const [playlistToRemoveFromZone, setPlaylistToRemoveFromZone] =
		useState<Playlist | null>(null)
	const [scheduleToRemoveFromZone, setScheduleToRemoveFromZone] =
		useState<Schedule | null>(null)
	const [groupAssignedContentToDetach, setGroupAssignedContentToDetach] =
		useState<{
			contentType: GroupAssignedContentType
			groupId: number
			groupName: string
		} | null>(null)
	const [playlistToRemoveFromBoth, setPlaylistToRemoveFromBoth] = useState<{
		playlist: Playlist
		groupId: number
		groupName: string
	} | null>(null)
	const [scheduleToRemoveFromBoth, setScheduleToRemoveFromBoth] = useState<{
		schedule: Schedule
		groupId: number
		groupName: string
	} | null>(null)
	const [addContentModalVisible, setAddContentModalVisible] = useState(false)
	const [addContentModalTab, setAddContentModalTab] =
		useState<ZoneContentTab>('playlists')
	const [isAddingContent, setIsAddingContent] = useState(false)
	const [campaignComingSoonModalVisible, setCampaignComingSoonModalVisible] =
		useState(false)

	useEffect(() => {
		const state = location.state as ZoneDetailsLocationState | null
		if (!state?.openAddContentModal) return

		setAddContentModalTab(state.addContentInitialTab ?? 'playlists')
		setAddContentModalVisible(true)
		window.history.replaceState({}, document.title)
	}, [location.state])

	// Use the update zone mutation
	const {
		mutate: updateZone,
		isPending: isUpdatingZone,
		isError: hasUpdateZoneError,
	} = useUpdateZone({
		config: useAccountConfig({}),
		options: {
			onSuccess: () => {
				showNotification({
					type: 'success',
					message: t('entities.zone.edit.success.title'),
				})
				refetchZone()
			},
			onError: (error: Error) => {
				showNotification({
					type: 'error',
					message: t('entities.zone.edit.error.title'),
					description: extractErrorMessage(error, t('common.try_again_later')),
				})
			},
		},
	})

	// Use the update group zones mutation to remove zone from group
	const { isPending: isRemovingFromGroup } = useUpdateGroupZones({
		config: useAccountConfig({}),
		options: {
			onSuccess: () => {
				showNotification({
					type: 'success',
					message: t('entities.group.remove_zone.success.title'),
					description: t('entities.group.remove_zone.success.description', {
						groupName: selectedGroupToRemove?.name,
					}),
				})
				setRemoveGroupModalVisible(false)
				setSelectedGroupToRemove(null)
				refetchZone()
			},
			onError: (error: Error) => {
				console.error('Error removing zone from group:', error)
				showNotification({
					type: 'error',
					message: t('entities.group.remove_zone.error.title'),
					description: t('entities.group.remove_zone.error.description'),
				})
			},
		},
	})

	// Use the update group zones mutation for batch operations (add zone to multiple groups)
	const {
		mutateAsync: updateGroupZonesAsync,
		isPending: isUpdatingGroupZones,
	} = useUpdateGroupZones({
		config: useAccountConfig({}),
	})

	// Fetch all groups (needed for add/remove operations)
	const { data: groupsResponse } = useGroups({
		config: useAccountConfig({
			params: {
				per_page: 100,
				include:
					'players,announcements.adverts.audios,schedules.timeslots.timeslot_contents',
			},
		}),
	})

	const allGroups = groupsResponse?.data || []

	const handleUpdateZone = (values: UpdateZoneRequestDTO) => {
		if (!zoneId) return
		updateZone({ zoneId: Number(zoneId), data: values })
	}

	const handleChangeAdvertAudioVersion = (advertAudioVersionId: number) => {
		if (!zoneId || !currentZone) return
		updateZone({
			zoneId: Number(zoneId),
			data: {
				name: currentZone.name,
				advert_audio_version_id: advertAudioVersionId,
			},
		})
	}

	const handleEditZone = () => {
		setEditZoneModalVisible(true)
	}

	const handleSaveEditZone = (values: UpdateZoneRequestDTO) => {
		if (!zoneId) return

		if (values.name) {
			zoneDraft.updateZoneProperties({
				name: values.name,
				storeId: values.store_id ?? currentZone?.storedId ?? 0,
			})
		}

		showNotification({
			type: 'info',
			message: t('common.unsavedChanges'),
			description: t('actions.saveChanges'),
		})

		setEditZoneModalVisible(false)
	}

	const handleCloseEditZoneModal = () => {
		setEditZoneModalVisible(false)
	}

	const { mutate: exportZoneConnections } = useExportZoneConnections({
		config: useAccountConfig({}),
		options: {
			onSuccess: (data: Blob) => {
				try {
					// Create a URL for the blob
					const url = window.URL.createObjectURL(data)

					// Create a temporary anchor element for download
					const a = document.createElement('a')
					a.href = url

					// Use .xlsx extension since the server is returning an Excel file
					a.download = `zones_${currentZone?.name || 'export'}_${new Date().toISOString().split('T')[0]}.xlsx`

					// Trigger click to start download
					document.body.appendChild(a)
					a.click()

					// Clean up
					window.URL.revokeObjectURL(url)
					document.body.removeChild(a)
				} catch (error) {
					console.error('Error downloading file:', error)
					showNotification({
						type: 'error',
						message: t('common.download_error'),
						description: t('common.try_again_later'),
					})
				}
			},
			onError: (error: Error) => {
				console.error('Error exporting zones:', error)
				showNotification({
					type: 'error',
					message: t('common.download_error'),
					description: t('common.try_again_later'),
				})
			},
		},
	})

	const onDateRangeChange = (startDate: DateTime, endDate: DateTime) => {
		setStartDate(startDate)
		setEndDate(endDate)
	}

	const onDownloadReport = () => {
		exportZoneConnections({
			uuid: currentZone?.uuid || '',
			from: startDate.toISODate() || '',
			to: endDate.toISODate() || '',
			offset: -(startDate.offset || endDate.offset),
		})
	}

	const hasBoxControls = currentZone?.getActiveBoxAllocation() !== null

	// Redirect to zones list if there's no zoneId.
	// For 403/404 (access denied), we show a warning modal instead of redirecting immediately.
	if (!zoneId) {
		navigate('/zones')
		return null
	}

	if (error && !isLoading && !isAccessDeniedError(error)) {
		navigate('/zones')
		return null
	}

	// Action handlers for InformationTab
	const handlePlaylistClick = (playlistId: number) => {
		navigate(`/music/playlists/${playlistId}`)
	}

	const handleCampaignClick = (campaignId: number) => {
		navigate(`/campaigns/${campaignId}`)
	}

	const handleAddPlaylist = () => {
		setAddContentModalTab('playlists')
		setAddContentModalVisible(true)
	}

	const handleAddSchedule = () => {
		setAddContentModalTab('schedules')
		setAddContentModalVisible(true)
	}

	const handleAddCampaign = () => {
		setCampaignComingSoonModalVisible(true)
	}

	const handleRemoveCampaign = (_campaignId: number) => {
		setCampaignComingSoonModalVisible(true)
	}

	const handleCloseAddContentModal = () => {
		if (isAddingContent) return
		setAddContentModalVisible(false)
	}

	const handleConfirmAddContent = async (
		selectedPlaylistIds: number[],
		selectedScheduleIds: number[]
	) => {
		if (!zoneId) return

		const directPlaylistIds = new Set(
			directPlaylists.map((playlist) => playlist.id)
		)
		const directScheduleIds = new Set(
			directSchedules.map((schedule) => schedule.id)
		)

		const selectedPlaylistIdSet = new Set(selectedPlaylistIds)
		const selectedScheduleIdSet = new Set(selectedScheduleIds)

		const playlistIdsToAdd = selectedPlaylistIds.filter(
			(id) => !directPlaylistIds.has(id)
		)
		const scheduleIdsToAdd = selectedScheduleIds.filter(
			(id) => !directScheduleIds.has(id)
		)
		const playlistIdsToRemove = directPlaylists
			.map((p) => p.id)
			.filter((id) => !selectedPlaylistIdSet.has(id))
		const scheduleIdsToRemove = directSchedules
			.map((s) => s.id)
			.filter((id) => !selectedScheduleIdSet.has(id))

		if (
			playlistIdsToAdd.length === 0 &&
			scheduleIdsToAdd.length === 0 &&
			playlistIdsToRemove.length === 0 &&
			scheduleIdsToRemove.length === 0
		) {
			setAddContentModalVisible(false)
			return
		}

		setIsAddingContent(true)
		try {
			const addRequests: Promise<unknown>[] = []

			if (playlistIdsToAdd.length > 0) {
				addRequests.push(
					addPlaylistsToZone({
						zoneId: Number(zoneId),
						data: { playlists: playlistIdsToAdd },
					})
				)
			}

			if (scheduleIdsToAdd.length > 0) {
				addRequests.push(
					addSchedulesToZone({
						zoneId: Number(zoneId),
						data: { schedules: scheduleIdsToAdd },
					})
				)
			}

			if (playlistIdsToRemove.length > 0) {
				addRequests.push(
					removePlaylistsFromZone({
						zoneId: Number(zoneId),
						data: { playlists: playlistIdsToRemove },
					})
				)
			}

			if (scheduleIdsToRemove.length > 0) {
				addRequests.push(
					removeSchedulesFromZone({
						zoneId: Number(zoneId),
						data: { schedules: scheduleIdsToRemove },
					})
				)
			}

			await Promise.all(addRequests)

			queryClient.invalidateQueries({ queryKey: ['zone'] })
			queryClient.invalidateQueries({ queryKey: ['zone-playlists'] })
			queryClient.invalidateQueries({ queryKey: ['zone-schedules'] })
			refetchZone()

			showNotification({
				type: 'success',
				message: tZones('details.addContentModal.successTitle'),
				description: tZones('details.addContentModal.successDescription', {
					playlistsCount: playlistIdsToAdd.length,
					schedulesCount: scheduleIdsToAdd.length,
				}),
			})
			setAddContentModalVisible(false)
		} catch (error) {
			showNotification({
				type: 'error',
				message: tZones('details.addContentModal.errorTitle'),
				description: extractErrorMessage(
					error,
					tZones('details.addContentModal.errorDescription')
				),
			})
		} finally {
			setIsAddingContent(false)
		}
	}

	const handleRemovePlaylistFromZone = (playlist: Playlist) => {
		setPlaylistToRemoveFromZone(playlist)
	}

	const handleConfirmRemovePlaylistFromZone = async () => {
		if (!zoneId || !playlistToRemoveFromZone) return
		try {
			await removePlaylistsFromZone({
				zoneId: Number(zoneId),
				data: { playlists: [playlistToRemoveFromZone.id] },
			})
			queryClient.invalidateQueries({ queryKey: ['zone-playlists'] })
			refetchZone()
			showNotification({
				type: 'success',
				message: t('entities.group.remove_direct_playlist.success.title'),
			})
			setPlaylistToRemoveFromZone(null)
		} catch (error) {
			console.error('Error removing playlist from zone:', error)
			setPlaylistToRemoveFromZone(null)
			if (isForbiddenError(error)) {
				setPlaylistActionDeniedModalOpen(true)
			} else {
				showNotification({
					type: 'error',
					message: t('entities.group.remove_direct_playlist.error.title'),
					description: extractErrorMessage(error, t('common.try_again_later')),
				})
			}
		}
	}

	const handleRemovePlaylistFromGroup = (playlist: Playlist) => {
		// Find which group this playlist belongs to
		const group = currentZone?.groups?.find((g) =>
			g.playlists?.some((p) => p.id === playlist.id)
		)
		if (!group) return

		setGroupAssignedContentToDetach({
			contentType: 'playlist',
			groupId: group.id,
			groupName: group.name,
		})
	}

	const handleRemoveScheduleFromZone = (schedule: Schedule) => {
		setScheduleToRemoveFromZone(schedule)
	}

	const handleConfirmRemoveScheduleFromZone = async () => {
		if (!zoneId || !scheduleToRemoveFromZone) return

		try {
			await removeSchedulesFromZone({
				zoneId: Number(zoneId),
				data: { schedules: [scheduleToRemoveFromZone.id] },
			})
			queryClient.invalidateQueries({ queryKey: ['zone-schedules'] })
			refetchZone()
			showNotification({
				type: 'success',
				message: t('entities.zone.remove_schedule.success.title'),
			})
			setScheduleToRemoveFromZone(null)
		} catch (error) {
			console.error('Error removing schedule from zone:', error)
			setScheduleToRemoveFromZone(null)
			if (isForbiddenError(error)) {
				setScheduleActionDeniedModalOpen(true)
			} else {
				showNotification({
					type: 'error',
					message: t('entities.zone.remove_schedule.error.title'),
					description: extractErrorMessage(error, t('common.try_again_later')),
				})
			}
		}
	}

	const handleRemoveScheduleFromGroup = (schedule: Schedule) => {
		const group = currentZone?.groups?.find((g) =>
			g.schedules?.some((s) => s.id === schedule.id)
		)
		if (!group) return

		setGroupAssignedContentToDetach({
			contentType: 'schedule',
			groupId: group.id,
			groupName: group.name,
		})
	}

	const handleRemovePlaylistFromBoth = (playlist: Playlist) => {
		const group = currentZone?.groups?.find((g) =>
			g.playlists?.some((p) => p.id === playlist.id)
		)
		if (!group) return
		setPlaylistToRemoveFromBoth({
			playlist,
			groupId: group.id,
			groupName: group.name,
		})
	}

	const handleConfirmRemovePlaylistFromBoth = async () => {
		if (!zoneId || !playlistToRemoveFromBoth || !currentZone) return
		const { playlist, groupId } = playlistToRemoveFromBoth
		const group = allGroups.find((g: Group) => g.id === groupId)
		if (!group) return
		try {
			const updatedZoneIds = (group.zones || [])
				.map((z) => z.id)
				.filter((id) => id !== currentZone.id)
			await Promise.all([
				removePlaylistsFromZone({
					zoneId: Number(zoneId),
					data: { playlists: [playlist.id] },
				}),
				updateGroupZonesAsync({
					groupId,
					data: { players: updatedZoneIds },
				}),
			])
			queryClient.invalidateQueries({ queryKey: ['zone'] })
			queryClient.invalidateQueries({ queryKey: ['zone-playlists'] })
			refetchZone()
			showNotification({
				type: 'success',
				message: t('entities.group.remove_direct_playlist.success.title'),
			})
			setPlaylistToRemoveFromBoth(null)
		} catch (error) {
			console.error('Error removing playlist from both:', error)
			setPlaylistToRemoveFromBoth(null)
			if (isForbiddenError(error)) {
				setPlaylistActionDeniedModalOpen(true)
			} else {
				showNotification({
					type: 'error',
					message: t('entities.group.remove_direct_playlist.error.title'),
					description: extractErrorMessage(error, t('common.try_again_later')),
				})
			}
		}
	}

	const handleRemoveScheduleFromBoth = (schedule: Schedule) => {
		const group = currentZone?.groups?.find((g) =>
			g.schedules?.some((s) => s.id === schedule.id)
		)
		if (!group) return
		setScheduleToRemoveFromBoth({
			schedule,
			groupId: group.id,
			groupName: group.name,
		})
	}

	const handleConfirmRemoveScheduleFromBoth = async () => {
		if (!zoneId || !scheduleToRemoveFromBoth || !currentZone) return
		const { schedule, groupId } = scheduleToRemoveFromBoth
		const group = allGroups.find((g: Group) => g.id === groupId)
		if (!group) return
		try {
			const updatedZoneIds = (group.zones || [])
				.map((z) => z.id)
				.filter((id) => id !== currentZone.id)
			await Promise.all([
				removeSchedulesFromZone({
					zoneId: Number(zoneId),
					data: { schedules: [schedule.id] },
				}),
				updateGroupZonesAsync({
					groupId,
					data: { players: updatedZoneIds },
				}),
			])
			queryClient.invalidateQueries({ queryKey: ['zone'] })
			queryClient.invalidateQueries({ queryKey: ['zone-schedules'] })
			refetchZone()
			showNotification({
				type: 'success',
				message: t('entities.zone.remove_schedule.success.title'),
			})
			setScheduleToRemoveFromBoth(null)
		} catch (error) {
			console.error('Error removing schedule from both:', error)
			setScheduleToRemoveFromBoth(null)
			if (isForbiddenError(error)) {
				setScheduleActionDeniedModalOpen(true)
			} else {
				showNotification({
					type: 'error',
					message: t('entities.zone.remove_schedule.error.title'),
					description: extractErrorMessage(error, t('common.try_again_later')),
				})
			}
		}
	}

	const handleConfirmDetachZoneFromGroup = async () => {
		if (!groupAssignedContentToDetach || !currentZone) return

		const group = allGroups.find(
			(currentGroup: Group) =>
				currentGroup.id === groupAssignedContentToDetach.groupId
		)

		if (!group) {
			showNotification({
				type: 'error',
				message: t('entities.group.remove_zone.error.title'),
				description: t('entities.group.remove_zone.error.description'),
			})
			return
		}

		try {
			const currentZoneIds = (group.zones || []).map((zone) => zone.id)
			const updatedZoneIds = currentZoneIds.filter(
				(id) => id !== currentZone.id
			)

			await updateGroupZonesAsync({
				groupId: group.id,
				data: { players: updatedZoneIds },
			})

			queryClient.invalidateQueries({ queryKey: ['zone'] })
			queryClient.invalidateQueries({ queryKey: ['zone-playlists'] })
			queryClient.invalidateQueries({ queryKey: ['zone-schedules'] })
			refetchZone()

			showNotification({
				type: 'success',
				message: t('entities.group.remove_zone.success.title'),
				description: t('entities.group.remove_zone.success.description', {
					groupName: groupAssignedContentToDetach.groupName,
				}),
			})

			setGroupAssignedContentToDetach(null)
		} catch (error) {
			console.error('Error removing zone from group:', error)
			showNotification({
				type: 'error',
				message: t('entities.group.remove_zone.error.title'),
				description: t('entities.group.remove_zone.error.description'),
			})
		}
	}

	const handleAddGroup = () => {
		setAddToGroupsModalVisible(true)
	}

	const handleConfirmAddToGroups = (
		selectedGroupIds: number[],
		_initialGroupIds: number[]
	) => {
		if (!currentZone) return

		const selectedGroups = allGroups.filter((g: Group) =>
			selectedGroupIds.includes(g.id)
		)
		zoneDraft.setGroups(selectedGroups)

		showNotification({
			type: 'info',
			message: t('common.unsavedChanges'),
			description: t('actions.saveChanges'),
		})

		setAddToGroupsModalVisible(false)
	}

	const handleCloseAddToGroupsModal = () => {
		setAddToGroupsModalVisible(false)
	}

	const handleRemoveGroup = (groupId: number) => {
		// Find the group to remove
		const groupToRemove = currentZone?.groups?.find((g) => g.id === groupId)
		if (!groupToRemove) return

		// Show confirmation modal
		setSelectedGroupToRemove(groupToRemove)
		setRemoveGroupModalVisible(true)
	}

	const handleConfirmRemoveGroup = () => {
		if (!selectedGroupToRemove || !currentZone) return

		zoneDraft.removeGroups([selectedGroupToRemove.id])

		showNotification({
			type: 'info',
			message: t('common.unsavedChanges'),
			description: t('actions.saveChanges'),
		})

		setRemoveGroupModalVisible(false)
		setSelectedGroupToRemove(null)
	}

	const handleCloseRemoveGroupModal = () => {
		if (isRemovingFromGroup) return // Prevent closing while removing
		setRemoveGroupModalVisible(false)
		setSelectedGroupToRemove(null)
	}

	const handleDeleteZone = () => {
		if (currentZone) {
			zoneDeletion.openDeleteModal(currentZone)
		}
	}

	const handleRefreshZone = () => {
		if (currentZone) {
			zoneRefresh.openRefreshModal(currentZone)
		}
	}

	const handleSaveChanges = async () => {
		if (!currentZone) return

		const { addedGroups, removedGroups, propertiesChanged, properties } =
			zoneDraft.getChanges()

		zoneDraft.setSaving(true)

		try {
			const updatePromises: Promise<unknown>[] = []

			// Save zone properties if changed
			if (propertiesChanged && properties) {
				updatePromises.push(
					new Promise((resolve, reject) => {
						updateZone(
							{
								zoneId: Number(zoneId),
								data: {
									name: properties.name,
									store_id: properties.storeId,
									flags: properties.flags,
								},
							},
							{
								onSuccess: resolve,
								onError: reject,
							}
						)
					})
				)
			}

			// Add zone to groups
			addedGroups.forEach((groupId) => {
				const group = allGroups.find((g: Group) => g.id === groupId)
				if (group) {
					const currentZoneIds = (group.zones || []).map((z) => z.id)
					const updatedZoneIds = [...currentZoneIds, currentZone.id]

					updatePromises.push(
						updateGroupZonesAsync({
							groupId,
							data: { players: updatedZoneIds },
						})
					)
				} else {
					console.error(`[Add] Group ${groupId} not found in allGroups`, {
						allGroupIds: allGroups.map((g) => g.id),
					})
				}
			})

			// Remove zone from groups
			removedGroups.forEach((groupId) => {
				const group = allGroups.find((g: Group) => g.id === groupId)
				if (group) {
					const currentZoneIds = (group.zones || []).map((z) => z.id)
					const updatedZoneIds = currentZoneIds.filter(
						(id) => id !== currentZone.id
					)

					updatePromises.push(
						updateGroupZonesAsync({
							groupId,
							data: { players: updatedZoneIds },
						})
					)
				} else {
					console.error(`[Remove] Group ${groupId} not found in allGroups`, {
						allGroupIds: allGroups.map((g) => g.id),
						removedGroups,
					})
				}
			})

			await Promise.all(updatePromises)

			showNotification({
				type: 'success',
				message: t('actions.saveChanges'),
				description: t('entities.zone.edit.success.description'),
			})

			const { data: freshZoneData } = await refetchZone()

			if (freshZoneData) {
				zoneDraft.updateWithFreshData(freshZoneData)
			}
		} catch (error) {
			console.error('Error saving zone changes:', error)
			showNotification({
				type: 'error',
				message: t('entities.zone.edit.error.title'),
				description: t('entities.zone.edit.error.description'),
			})
		} finally {
			zoneDraft.setSaving(false)
		}
	}

	const handleDiscardChanges = () => {
		zoneDraft.resetDraft()
		showNotification({
			type: 'info',
			message: t('actions.changesDiscarded'),
			description: t('common.unsavedChanges'),
		})
	}

	const handleDraftFlags = (flags: string[]) => {
		zoneDraft.updateZoneProperties({ flags })
	}

	const zoneWithDraft = currentZone
		? Zone.create({
				...currentZone,
				groups: zoneDraft.current,
				name: zoneDraft.currentProperties?.name ?? currentZone.name,
				storedId: zoneDraft.currentProperties?.storeId ?? currentZone.storedId,
				flags: zoneDraft.currentProperties?.flags ?? currentZone.flags ?? [],
			})
		: undefined

	return (
		<>
			<Dialog
				open={accessDeniedModalOpen}
				onOpenChange={(open) => {
					if (!open) {
						setAccessDeniedModalOpen(false)
						navigate(redirectToRef.current, { replace: true })
						return
					}
					setAccessDeniedModalOpen(true)
				}}
			>
				<DialogContent className="sm:max-w-[520px]">
					<DialogHeader>
						<DialogTitle>{t('zones.accessDenied.title')}</DialogTitle>
						<DialogDescription>
							{t('zones.accessDenied.description')}
						</DialogDescription>
					</DialogHeader>
					<DialogFooter>
						<Button
							variant="outline"
							onClick={() => window.open('https://wa.me/15557052438', '_blank')}
						>
							{t('actions.contactSupport')}
						</Button>
						<Button
							onClick={() => {
								setAccessDeniedModalOpen(false)
								navigate(redirectToRef.current, { replace: true })
							}}
						>
							{t('actions.close')}
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>

			<Dialog
				open={scheduleActionDeniedModalOpen}
				onOpenChange={setScheduleActionDeniedModalOpen}
			>
				<DialogContent className="sm:max-w-[520px]">
					<DialogHeader>
						<DialogTitle>{t('schedules.accessDenied.title')}</DialogTitle>
						<DialogDescription>
							{t('schedules.accessDenied.description')}
						</DialogDescription>
					</DialogHeader>
					<DialogFooter>
						<Button
							variant="outline"
							onClick={() => window.open('https://wa.me/15557052438', '_blank')}
						>
							{t('actions.contactSupport')}
						</Button>
						<Button onClick={() => setScheduleActionDeniedModalOpen(false)}>
							{t('actions.close')}
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>

			<Dialog
				open={playlistActionDeniedModalOpen}
				onOpenChange={setPlaylistActionDeniedModalOpen}
			>
				<DialogContent className="sm:max-w-[520px]">
					<DialogHeader>
						<DialogTitle>{t('playlists.accessDenied.title')}</DialogTitle>
						<DialogDescription>
							{t('playlists.accessDenied.description')}
						</DialogDescription>
					</DialogHeader>
					<DialogFooter>
						<Button
							variant="outline"
							onClick={() => window.open('https://wa.me/15557052438', '_blank')}
						>
							{t('actions.contactSupport')}
						</Button>
						<Button onClick={() => setPlaylistActionDeniedModalOpen(false)}>
							{t('actions.close')}
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>

			<RemoveZoneFromGroupModal
				visible={removeGroupModalVisible}
				zone={currentZone ?? null}
				group={selectedGroupToRemove}
				onClose={handleCloseRemoveGroupModal}
				onConfirm={handleConfirmRemoveGroup}
				isRemoving={isRemovingFromGroup}
			/>
			<AddGroupToZone
				visible={addToGroupsModalVisible}
				zone={zoneWithDraft ?? null}
				onClose={handleCloseAddToGroupsModal}
				onConfirm={handleConfirmAddToGroups}
				isAssigning={false}
			/>
			<EditZoneModal
				visible={editZoneModalVisible}
				zone={zoneWithDraft ?? null}
				onClose={handleCloseEditZoneModal}
				onSubmit={handleSaveEditZone}
				isLoading={false}
			/>
			<DeleteZoneModal
				visible={zoneDeletion.isModalVisible}
				zone={zoneDeletion.selectedZone}
				onClose={zoneDeletion.closeDeleteModal}
				onConfirm={zoneDeletion.handleDeleteZone}
				isDeleting={zoneDeletion.isDeleting}
				onPage="zones"
			/>

			<RefreshZoneModal
				visible={zoneRefresh.isModalVisible}
				onClose={zoneRefresh.closeRefreshModal}
				onConfirm={zoneRefresh.handleRefreshZone}
				isRefreshing={zoneRefresh.isRefreshing}
			/>

			<ConfirmationModal
				visible={!!playlistToRemoveFromZone}
				title={t('entities.zone.remove_playlist.title', 'Remove Playlist')}
				description={t('entities.zone.remove_playlist.description', {
					playlistName: playlistToRemoveFromZone?.title,
				})}
				onConfirm={handleConfirmRemovePlaylistFromZone}
				onClose={() => setPlaylistToRemoveFromZone(null)}
				isLoading={isRemovingPlaylistFromZone}
			/>

			<ConfirmationModal
				visible={!!scheduleToRemoveFromZone}
				title={t('entities.zone.remove_schedule.title')}
				description={t('entities.zone.remove_schedule.description', {
					scheduleName: scheduleToRemoveFromZone?.title,
				})}
				onConfirm={handleConfirmRemoveScheduleFromZone}
				onClose={() => setScheduleToRemoveFromZone(null)}
				isLoading={isRemovingScheduleFromZone}
			/>

			<ConfirmationModal
				visible={!!groupAssignedContentToDetach}
				title={
					groupAssignedContentToDetach?.contentType === 'schedule'
						? t(
								'entities.zone.remove_zone_from_group_schedule_assignment.title'
							)
						: t(
								'entities.zone.remove_zone_from_group_playlist_assignment.title'
							)
				}
				description={
					groupAssignedContentToDetach?.contentType === 'schedule'
						? t(
								'entities.zone.remove_zone_from_group_schedule_assignment.description',
								{
									groupName: groupAssignedContentToDetach.groupName,
								}
							)
						: t(
								'entities.zone.remove_zone_from_group_playlist_assignment.description',
								{
									groupName: groupAssignedContentToDetach?.groupName,
								}
							)
				}
				onConfirm={handleConfirmDetachZoneFromGroup}
				onClose={() => setGroupAssignedContentToDetach(null)}
				isLoading={isUpdatingGroupZones}
			/>

			<ConfirmationModal
				visible={!!playlistToRemoveFromBoth}
				title={t('entities.zone.remove_playlist_from_both.title')}
				description={t('entities.zone.remove_playlist_from_both.description', {
					playlistName: playlistToRemoveFromBoth?.playlist.title,
					groupName: playlistToRemoveFromBoth?.groupName,
				})}
				confirmText={t('entities.zone.remove_playlist_from_both.confirm')}
				onConfirm={handleConfirmRemovePlaylistFromBoth}
				onClose={() => setPlaylistToRemoveFromBoth(null)}
				isLoading={isRemovingPlaylistFromZone || isUpdatingGroupZones}
			/>

			<ConfirmationModal
				visible={!!scheduleToRemoveFromBoth}
				title={t('entities.zone.remove_schedule_from_both.title')}
				description={t('entities.zone.remove_schedule_from_both.description', {
					scheduleName: scheduleToRemoveFromBoth?.schedule.title,
					groupName: scheduleToRemoveFromBoth?.groupName,
				})}
				confirmText={t('entities.zone.remove_schedule_from_both.confirm')}
				onConfirm={handleConfirmRemoveScheduleFromBoth}
				onClose={() => setScheduleToRemoveFromBoth(null)}
				isLoading={isRemovingScheduleFromZone || isUpdatingGroupZones}
			/>

			<AddContentToZoneModal
				visible={addContentModalVisible}
				initialTab={addContentModalTab}
				initialPlaylistIds={initialPlaylistIds}
				initialScheduleIds={initialScheduleIds}
				onClose={handleCloseAddContentModal}
				onConfirm={handleConfirmAddContent}
				isSubmitting={isAddingContent}
				groupPlaylistConflicts={groupPlaylistConflicts}
				groupScheduleConflicts={groupScheduleConflicts}
			/>

			<ConfirmationModal
				visible={campaignComingSoonModalVisible}
				title={tZones('details.campaigns.comingSoonModal.title')}
				description={tZones('details.campaigns.comingSoonModal.description')}
				confirmText={tZones('details.campaigns.comingSoonModal.redirectCta')}
				cancelText={t('actions.cancel')}
				variant="default"
				onConfirm={() => {
					setCampaignComingSoonModalVisible(false)
					navigate('/zones?tab=group')
				}}
				onClose={() => setCampaignComingSoonModalVisible(false)}
			/>

			<ZoneDetailsUI
				zone={zoneWithDraft}
				zoneConnections={zoneConnections}
				zoneEvents={zoneEvents}
				isLoading={isLoading}
				isUpdatingZone={isUpdatingZone}
				hasUpdateZoneError={hasUpdateZoneError}
				isZoneEventsLoading={isZoneEventsLoading || isZoneEventsFetching}
				isZoneConnectionsLoading={
					isZoneConnectionsLoading || isZoneConnectionsFetching
				}
				zoneEventsError={zoneEventsError}
				zoneConnectionsError={zoneConnectionsError}
				hasBoxControls={hasBoxControls}
				occupationData={occupationData}
				isLoadingOccupation={isLoadingOccupation}
				directPlaylists={directPlaylists}
				directSchedules={directSchedules}
				isLoadingDirectPlaylists={isLoadingDirectPlaylists}
				onDownloadReport={onDownloadReport}
				onDateRangeChange={onDateRangeChange}
				activeTab={activeTab}
				onTabChange={handleTabChange}
				onUpdateZone={handleUpdateZone}
				onDraftFlags={handleDraftFlags}
				onEditZone={handleEditZone}
				onPlaylistClick={handlePlaylistClick}
				onCampaignClick={handleCampaignClick}
				onAddPlaylist={handleAddPlaylist}
				onAddSchedule={handleAddSchedule}
				onAddCampaign={handleAddCampaign}
				onRemoveCampaign={handleRemoveCampaign}
				onAddGroup={handleAddGroup}
				onRemoveGroup={handleRemoveGroup}
				onRemovePlaylistFromZone={handleRemovePlaylistFromZone}
				onRemovePlaylistFromGroup={handleRemovePlaylistFromGroup}
				onRemovePlaylistFromBoth={handleRemovePlaylistFromBoth}
				onRemoveScheduleFromZone={handleRemoveScheduleFromZone}
				onRemoveScheduleFromGroup={handleRemoveScheduleFromGroup}
				onRemoveScheduleFromBoth={handleRemoveScheduleFromBoth}
				onDeleteZone={handleDeleteZone}
				onRefreshZone={handleRefreshZone}
				onChangeAdvertAudioVersion={handleChangeAdvertAudioVersion}
				isUpdatingAdvertAudioVersion={isUpdatingZone}
				hasUnsavedChanges={zoneDraft.hasChanges}
				isSaving={zoneDraft.isSaving}
				onSaveChanges={handleSaveChanges}
				onDiscardChanges={handleDiscardChanges}
				hasAdvertAudioVersionFeature={hasAdvertAudioVersionFeature}
			/>
		</>
	)
}

const ZoneDetails: React.FC = () => {
	const { zoneId } = useParams<{ zoneId: string }>()
	const navigate = useNavigate()

	const zoneInclude =
		'box_allocations,state,groups.announcements,groups.schedules,groups.playlists,store.lastGooglePlaceData,last_played_song,last_played_schedule,advert_audio_version,flags'

	if (!zoneId || Number.isNaN(Number(zoneId))) {
		navigate('/zones')
		return null
	}

	const { data: zone } = useZone({
		playerId: Number(zoneId),
		config: useAccountConfig({
			params: {
				include: zoneInclude,
			},
		}),
	})

	return (
		<ZoneDraftProvider key={zoneId} initialZone={zone ?? null}>
			<ZoneDetailsContent />
		</ZoneDraftProvider>
	)
}

export default ZoneDetails
